/**
 * ControlGroupEditorDialog
 * Allows editing a control group: set/clear master device, add/remove/reorder grouped devices.
 * Device selection: choose unit (from property) then pick a device from that unit.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { gql, useMutation } from '@apollo/client';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import BorderedSection from '../shared/borderedSection';
import {
  UnitDetailDocument,
  UnitDevicesDocument,
  useControlGroupRefreshQuery,
  useDeleteControlGroupMutation,
  useUpdateControlGroupMutation,
  useUnitListByPropertyIdQuery,
  useUnitDevicesQuery,
} from '../../../types/generated-types';
import type { UpdateOneControlGroupInput } from '../../../types/generated-types';
import { Notifier } from '../../system/services/notificationManager';

const CREATE_CONTROL_GROUP_MUTATION = gql`
  mutation CreateControlGroup($record: CreateOneControlGroupInput!) {
    createControlGroup(record: $record) {
      _id
      name
      propertyId
      unitId
      type
      automationEnabled
      masterDeviceId
      groupedDevices {
        _id
        deviceId
        name
        type
        displayOrder
      }
      deviceDetails
    }
  }
`;

const DEFAULT_CONTROL_GROUP_ATTRIBUTES = {
  IndoorSensor: ['temperature.value', 'humidity.value'],
};

function generateGroupedDeviceId(): string {
  return `gd-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

type GroupedDeviceItem = {
  _id: string;
  deviceId: string;
  name: string;
  type: string;
  displayOrder: number;
};

export interface ControlGroupEditorDialogProps {
  open: boolean;
  controlGroupId?: string;
  propertyId: string;
  unitId: string;
  onClose: () => void;
  onSaved?: () => void;
}

export function ControlGroupEditorDialog({
  open,
  controlGroupId,
  propertyId,
  unitId,
  onClose,
  onSaved,
}: ControlGroupEditorDialogProps) {
  const isCreateMode = !controlGroupId;
  const [name, setName] = useState('');
  const [masterDeviceId, setMasterDeviceId] = useState<string | null>(null);
  const [masterDeviceType, setMasterDeviceType] = useState<string>('None');
  const [masterDeviceName, setMasterDeviceName] = useState<string>('');
  const [groupedDevices, setGroupedDevices] = useState<GroupedDeviceItem[]>([]);
  const [pickerMode, setPickerMode] = useState<'master' | null>(null);
  const [addDevicesModalOpen, setAddDevicesModalOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { data: groupData, loading: groupLoading } =
    useControlGroupRefreshQuery({
      variables: { controlGroupId: controlGroupId ?? '' },
      skip: !open || !controlGroupId,
    });

  const controlGroup = groupData?.controlGroupById ?? null;

  const { data: unitsData } = useUnitListByPropertyIdQuery({
    variables: { propertyId: propertyId || undefined },
    skip: !open || !propertyId,
  });
  const units = unitsData?.unitsByPropertyId ?? [];

  const needUnitDevices =
    open && selectedUnitId && (pickerMode === 'master' || addDevicesModalOpen);
  const { data: devicesData } = useUnitDevicesQuery({
    variables: { unitId: selectedUnitId },
    skip: !needUnitDevices,
  });
  const unitDevices = devicesData?.devices ?? [];
  const configuredUnitDevices = useMemo(
    () => unitDevices.filter((d) => d.isConfigured === true),
    [unitDevices],
  );
  const alreadySelectedDeviceIds = useMemo(
    () =>
      new Set([
        ...(masterDeviceId ? [masterDeviceId] : []),
        ...groupedDevices.map((d) => d.deviceId),
      ]),
    [masterDeviceId, groupedDevices],
  );
  const availableToSelectDevices = useMemo(
    () =>
      configuredUnitDevices.filter(
        (d) => !alreadySelectedDeviceIds.has(d.deviceId ?? ''),
      ),
    [configuredUnitDevices, alreadySelectedDeviceIds],
  );

  const refetchAfterControlGroupChange =
    unitId.length > 0
      ? [
          { query: UnitDetailDocument, variables: { id: unitId } },
          { query: UnitDevicesDocument, variables: { unitId } },
        ]
      : [];

  const [updateControlGroup, { loading: updating }] =
    useUpdateControlGroupMutation({
      onCompleted: () => {
        Notifier.success('Control group updated.');
        onSaved?.();
        onClose();
      },
      onError: (err) => {
        Notifier.error(err.message ?? 'Failed to update control group.');
      },
    });
  const [createControlGroup, { loading: creating }] = useMutation(
    CREATE_CONTROL_GROUP_MUTATION,
    {
      refetchQueries: refetchAfterControlGroupChange,
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notifier.success('Control group created.');
        onSaved?.();
        onClose();
      },
      onError: (err) => {
        Notifier.error(err.message ?? 'Failed to create control group.');
      },
    },
  );

  const [deleteControlGroup, { loading: deleting }] =
    useDeleteControlGroupMutation({
      refetchQueries: refetchAfterControlGroupChange,
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notifier.success('Control group removed.');
        setConfirmDeleteOpen(false);
        onSaved?.();
        onClose();
      },
      onError: (err) => {
        Notifier.error(err.message ?? 'Failed to remove control group.');
      },
    });

  const handleConfirmDelete = () => {
    if (!controlGroupId || deleting) return;
    deleteControlGroup({ variables: { id: controlGroupId } });
  };

  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setConfirmDeleteOpen(false);
      setName('');
      setMasterDeviceId(null);
      setMasterDeviceType('None');
      setMasterDeviceName('');
      setGroupedDevices([]);
      setPickerMode(null);
      setAddDevicesModalOpen(false);
      setSelectedUnitId('');
      return;
    }
    if (isCreateMode) {
      initializedRef.current = true;
      return;
    }
    if (!controlGroup || groupLoading) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    setName(controlGroup.name ?? '');
    setMasterDeviceId(controlGroup.masterDeviceId ?? null);
    setMasterDeviceType(
      controlGroup.masterDevice?.type ?? controlGroup.type ?? 'None',
    );
    setMasterDeviceName(
      controlGroup.masterDevice?.name ??
        controlGroup.masterDevice?.deviceId ??
        '',
    );
    const gd =
      controlGroup.groupedDevices
        ?.filter((d): d is NonNullable<typeof d> => d != null)
        .map((d, i) => ({
          _id: d._id,
          deviceId: d.deviceId,
          name: d.name ?? '',
          type: d.type ?? '',
          displayOrder: typeof d.displayOrder === 'number' ? d.displayOrder : i,
        }))
        .sort((a, b) => a.displayOrder - b.displayOrder) ?? [];
    setGroupedDevices(gd);
  }, [open, controlGroup, groupLoading, isCreateMode]);
  const initialized = isCreateMode || (!!controlGroup && !groupLoading);

  const handleSetMaster = () => setPickerMode('master');
  const handleClearMaster = () => setMasterDeviceId(null);
  const handleAddGroupedDevice = () => setAddDevicesModalOpen(true);
  const handlePickDeviceForMaster = (deviceId: string) => {
    const selected = configuredUnitDevices.find((d) => d.deviceId === deviceId);
    setMasterDeviceId(deviceId);
    setMasterDeviceType(selected?.type ?? 'None');
    setMasterDeviceName(selected?.name ?? selected?.deviceId ?? '');
    setPickerMode(null);
    setSelectedUnitId('');
  };
  const handlePickDeviceForGrouped = (
    deviceId: string,
    name: string,
    type: string,
  ) => {
    const nextOrder =
      groupedDevices.length === 0
        ? 0
        : Math.max(...groupedDevices.map((d) => d.displayOrder), -1) + 1;
    setGroupedDevices((prev) => [
      ...prev,
      {
        _id: generateGroupedDeviceId(),
        deviceId,
        name: name ?? '',
        type: type ?? '',
        displayOrder: nextOrder,
      },
    ]);
  };
  const sortedGroupedDevices = useMemo(
    () => [...groupedDevices].sort((a, b) => a.displayOrder - b.displayOrder),
    [groupedDevices],
  );

  const handleRemoveGrouped = (sortedIndex: number) => {
    const id = sortedGroupedDevices[sortedIndex]?._id;
    if (id == null) return;
    setGroupedDevices((prev) => {
      const next = prev.filter((d) => d._id !== id);
      return next.map((d, i) => ({ ...d, displayOrder: i }));
    });
  };
  const handleMoveUp = (sortedIndex: number) => {
    if (sortedIndex <= 0) return;
    const a = sortedGroupedDevices[sortedIndex];
    const b = sortedGroupedDevices[sortedIndex - 1];
    if (!a || !b) return;
    setGroupedDevices((prev) =>
      prev.map((d) =>
        d._id === a._id
          ? { ...d, displayOrder: b.displayOrder }
          : d._id === b._id
            ? { ...d, displayOrder: a.displayOrder }
            : d,
      ),
    );
  };
  const handleMoveDown = (sortedIndex: number) => {
    if (sortedIndex >= sortedGroupedDevices.length - 1) return;
    const a = sortedGroupedDevices[sortedIndex];
    const b = sortedGroupedDevices[sortedIndex + 1];
    if (!a || !b) return;
    setGroupedDevices((prev) =>
      prev.map((d) =>
        d._id === a._id
          ? { ...d, displayOrder: b.displayOrder }
          : d._id === b._id
            ? { ...d, displayOrder: a.displayOrder }
            : d,
      ),
    );
  };

  const handleSave = () => {
    const normalizedName = name.trim();
    if (!normalizedName) {
      Notifier.error('Control group name is required.');
      return;
    }
    if (groupedDevices.length < 1) {
      Notifier.error('Please add at least one grouped device.');
      return;
    }
    if (isCreateMode) {
      createControlGroup({
        variables: {
          record: {
            name: normalizedName,
            propertyId,
            unitId,
            type: masterDeviceId ? masterDeviceType : 'None',
            masterDeviceId: masterDeviceId ?? null,
            attributes: DEFAULT_CONTROL_GROUP_ATTRIBUTES,
            groupedDevices: groupedDevices.map((d) => ({
              _id: d._id,
              deviceId: d.deviceId,
              name: d.name,
              type: d.type,
              displayOrder: d.displayOrder,
            })),
          },
        },
      });
      return;
    }
    if (!controlGroupId) return;
    const record = {
      name: normalizedName,
      masterDeviceId: masterDeviceId ?? null,
      groupedDevices: groupedDevices.map((d) => ({
        _id: d._id,
        deviceId: d.deviceId,
        name: d.name,
        type: d.type,
        displayOrder: d.displayOrder,
      })),
    } as unknown as UpdateOneControlGroupInput;
    updateControlGroup({
      variables: {
        id: controlGroupId,
        record,
      },
    });
  };

  const masterDisplay = masterDeviceId
    ? `${masterDeviceName || masterDeviceId} (${masterDeviceType || '—'})`
    : 'None';

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isCreateMode ? 'Add Control Group' : 'Edit Control Group'}
        </DialogTitle>
        <DialogContent>
          {groupLoading || !initialized ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <BorderedSection title="Name">
                <TextField
                  fullWidth
                  size="small"
                  label="Control group name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </BorderedSection>
              <BorderedSection title="Master device">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {masterDisplay}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSetMaster}
                    disabled={!!pickerMode || addDevicesModalOpen}
                  >
                    Set master
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClearMaster}
                    disabled={!!pickerMode || !masterDeviceId}
                  >
                    Clear
                  </Button>
                </Box>
              </BorderedSection>

              <BorderedSection title="Grouped devices">
                <List dense disablePadding>
                  {sortedGroupedDevices.map((d, index) => (
                    <ListItem
                      key={d._id}
                      disablePadding
                      secondaryAction={
                        <Box
                          component="span"
                          sx={{ display: 'flex', gap: 0.5 }}
                        >
                          <Button
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleMoveUp(index)}
                            sx={{ minWidth: 36 }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            disabled={index === sortedGroupedDevices.length - 1}
                            onClick={() => handleMoveDown(index)}
                            sx={{ minWidth: 36 }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            onClick={() => handleRemoveGrouped(index)}
                            sx={{ minWidth: 36 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={d.name || d.deviceId}
                        secondary={`${d.type} · order ${d.displayOrder}`}
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddGroupedDevice}
                  disabled={addDevicesModalOpen}
                  sx={{ mt: 1 }}
                >
                  Add device
                </Button>
              </BorderedSection>

              {pickerMode === 'master' && (
                <BorderedSection title="Select master device">
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={selectedUnitId}
                      label="Unit"
                      onChange={(e) => setSelectedUnitId(e.target.value)}
                    >
                      {units.map((u) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.name ?? u._id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedUnitId ? (
                    <>
                      {availableToSelectDevices.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          All configured devices in this unit are already
                          selected.
                        </Typography>
                      ) : (
                        <List dense>
                          {availableToSelectDevices.map((dev) => (
                            <ListItem
                              key={dev.deviceId ?? dev.__typename}
                              disablePadding
                            >
                              <ListItemButton
                                onClick={() =>
                                  handlePickDeviceForMaster(dev.deviceId ?? '')
                                }
                              >
                                <ListItemText
                                  primary={dev.name ?? dev.deviceId ?? '—'}
                                  secondary={
                                    dev.type ?? dev.typeDisplayName ?? '—'
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a unit to list devices.
                    </Typography>
                  )}
                  <Button
                    size="small"
                    onClick={() => {
                      setPickerMode(null);
                      setSelectedUnitId('');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Cancel
                  </Button>
                </BorderedSection>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            {!isCreateMode && controlGroupId ? (
              <Button
                color="error"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={
                  groupLoading ||
                  !initialized ||
                  updating ||
                  creating ||
                  deleting
                }
              >
                Remove Control Group
              </Button>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: 'auto' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                groupLoading || !initialized || updating || creating || deleting
              }
            >
              {updating || creating
                ? isCreateMode
                  ? 'Saving…'
                  : 'Updating…'
                : isCreateMode
                  ? 'Save'
                  : 'Update'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => {
          if (!deleting) {
            setConfirmDeleteOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-control-group-dialog-title"
      >
        <DialogTitle id="delete-control-group-dialog-title">
          Remove control group?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently deletes this control group. You cannot undo this
            action.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDevicesModalOpen}
        onClose={() => setAddDevicesModalOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="add-devices-dialog-title"
      >
        <DialogTitle id="add-devices-dialog-title">
          Add devices to group
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              value={selectedUnitId}
              label="Unit"
              onChange={(e) => setSelectedUnitId(e.target.value)}
            >
              {units.map((u) => (
                <MenuItem key={u._id} value={u._id}>
                  {u.name ?? u._id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedUnitId ? (
            <>
              {availableToSelectDevices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  All configured devices in this unit are already selected.
                </Typography>
              ) : (
                <List dense>
                  {availableToSelectDevices.map((dev) => (
                    <ListItem
                      key={dev.deviceId ?? dev.__typename}
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() =>
                          handlePickDeviceForGrouped(
                            dev.deviceId ?? '',
                            dev.name ?? '',
                            dev.type ?? '',
                          )
                        }
                      >
                        <ListItemText
                          primary={dev.name ?? dev.deviceId ?? '—'}
                          secondary={dev.type ?? dev.typeDisplayName ?? '—'}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a unit to list devices.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddDevicesModalOpen(false)}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
